import { jsx as _jsx } from "react/jsx-runtime";
import { ViewTypeName } from "@air/types";
import noop from "lodash/noop";
import { createContext, memo, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTemporaryFeatureFlags } from "~/hooks/useTemporaryFeatureFlags";
import { setCurrentViewTypeAction } from "~/store/configViews/actions";
import { currentViewTypeNameSelector } from "~/store/configViews/selectors";
import { useCanViewPrivateAddOn } from "~/swr-hooks/workspaces/addOns/useCanViewPrivateAddOn";
import { DEFAULT_CONTEXT, getLastUsedSearchType, setLastUsedSearchType } from "~/utils/SearchUtils";
const defaultValue = {
    searchType: "keyword",
    setSearchType: noop,
    canUseSmartSearch: false,
    isSemanticSearchEnabled: false
};
export const PrivateSearchContext = /*#__PURE__*/ createContext(defaultValue);
export const PrivateSearchProvider = /*#__PURE__*/ memo((param)=>{
    let { children, supportsSmartSearch } = param;
    const dispatch = useDispatch();
    const currentViewTypeName = useSelector(currentViewTypeNameSelector);
    const { ui2 } = useTemporaryFeatureFlags();
    const { canViewAddOn: canViewSemanticSearch } = useCanViewPrivateAddOn({
        key: "semanticSearch"
    });
    const [searchType, setSearchType] = useState(defaultValue.searchType);
    const canUseSmartSearch = useMemo(()=>{
        return supportsSmartSearch && canViewSemanticSearch && ui2;
    }, [
        supportsSmartSearch,
        canViewSemanticSearch,
        ui2
    ]);
    useEffect(()=>{
        if (canUseSmartSearch) {
            setSearchType(getLastUsedSearchType(DEFAULT_CONTEXT));
        }
    }, [
        canUseSmartSearch,
        setSearchType
    ]);
    // Within a search session that supports smart search, we want to persist the search type
    useEffect(()=>{
        if (canUseSmartSearch) {
            setLastUsedSearchType(searchType, DEFAULT_CONTEXT);
        }
    }, [
        searchType,
        canUseSmartSearch
    ]);
    // Smart search is only supported in gallery view
    useEffect(()=>{
        if (searchType === "smart" && canUseSmartSearch && currentViewTypeName !== ViewTypeName.gallery) {
            dispatch(setCurrentViewTypeAction({
                viewTypeName: ViewTypeName.gallery
            }));
        }
    }, [
        searchType,
        canUseSmartSearch,
        dispatch,
        currentViewTypeName
    ]);
    const value = useMemo(()=>({
            searchType,
            setSearchType,
            canUseSmartSearch,
            isSemanticSearchEnabled: canUseSmartSearch && searchType === "smart"
        }), [
        searchType,
        canUseSmartSearch
    ]);
    return /*#__PURE__*/ _jsx(PrivateSearchContext.Provider, {
        value: value,
        children: children
    });
});
PrivateSearchProvider.displayName = "PrivateSearchProvider";
export const usePrivateSearchContext = ()=>useContext(PrivateSearchContext);
